import React from 'react';

import { BOTTOM_BAR_TEST_ID } from 'components/BottomBar';

/**
 * Distance the launcher has to sit above the viewport bottom to clear the fixed bottom action bar
 * the step forms render, or 0 when no bar is mounted.
 *
 * The bar is `position: fixed` at `bottom: 0`, full width, and lays its buttons out `flex-end`, so
 * its primary button lands in the same corner the launcher is pinned to and the two overlap on
 * every campaign / rule / reward step (user screenshot, PLA-8844). Nothing in the layout can be
 * measured from React here: the bar is rendered by ~18 unrelated form pages that know nothing about
 * Orbit, so the only shared handle is the DOM.
 *
 * Height is read off `getBoundingClientRect` rather than the ResizeObserver entry because the
 * launcher has to clear the bar's BORDER box - padding and the 1px top border included - and
 * `contentRect` reports neither. The observer is only the trigger.
 *
 * The commit is deferred to the next animation frame, as `useMeasuredWidth` does and for the same
 * reason: writing state from inside the ResizeObserver callback re-enters layout while that batch
 * of notifications is still being delivered, which is what produced the "ResizeObserver loop
 * completed with undelivered notifications" overlay during an Orbit open.
 *
 * Returns 0 in jsdom, where `getBoundingClientRect` reports a zero-height box, so every existing
 * launcher test renders at the unlifted corner exactly as before.
 */
const BAR_GAP = 14;

const MUTATION_DEBOUNCE_MS = 50;

const findBar = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-testid="${BOTTOM_BAR_TEST_ID}"]`);

const useBottomBarOffset = (): number => {
  const [offset, setOffset] = React.useState(0);
  // React re-renders once before bailing out of an identical `useState` value, so an unchanged
  // offset has to be caught before the call rather than inside the updater.
  const committed = React.useRef(0);

  React.useEffect(() => {
    let frame: number | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let observed: HTMLElement | null = null;

    const measure = () => {
      const bar = observed;
      const height = bar === null ? 0 : Math.round(bar.getBoundingClientRect().height);
      const next = height === 0 ? 0 : height + BAR_GAP;

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        frame = null;

        if (committed.current === next) {
          return;
        }

        committed.current = next;
        setOffset(next);
      });
    };

    const sync = () => {
      const bar = findBar();

      if (bar !== observed) {
        if (resizeObserver !== null) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        observed = bar;

        if (bar !== null && typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(measure);
          resizeObserver.observe(bar);
        }
      }

      measure();
    };

    sync();

    if (typeof MutationObserver === 'undefined') {
      return () => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
        }

        resizeObserver?.disconnect();
      };
    }

    // Every routed page swap runs through `document.body`, so the mount and unmount of a bar owned
    // by a page Orbit never renders arrives here and nowhere else. Debounced because a route change
    // delivers hundreds of records and only the last state of the tree matters.
    const mutationObserver = new MutationObserver(() => {
      if (debounce !== null) {
        clearTimeout(debounce);
      }

      debounce = setTimeout(sync, MUTATION_DEBOUNCE_MS);
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      if (debounce !== null) {
        clearTimeout(debounce);
      }

      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return offset;
};

export default useBottomBarOffset;
